import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { FaSignOutAlt, FaUser, FaQrcode, FaBell, FaCogs, FaPuzzlePiece, FaBullhorn, FaChartBar, FaWpforms, FaKey, FaExclamationTriangle } from 'react-icons/fa';
import api from './axiosSetup';
import cfg from '../config';
import BottomNav from './BottomNav';
import { useApp } from '../AppContext';

const CustomNavbar = () => {
  const navigate = useNavigate();
  const { isOn, appName, lizenz } = useApp();
  const [isAdmin, setIsAdmin]                 = useState(false);
  const [isAppAdmin, setIsAppAdmin]           = useState(false);
  const [unreadCount, setUnreadCount]         = useState(0);
  const [unseenChangelog, setUnseenChangelog] = useState(0);
  const [notifDropdown, setNotifDropdown]     = useState(false);
  const [recentNotifs, setRecentNotifs]       = useState([]);
  const notifRef = useRef(null);

  const fetchUnreadCount = useCallback(() => {
    api.get('/api/notifications/unread-count')
      .then(r => setUnreadCount(r.data.count || 0))
      .catch(() => {});
  }, []);

  const fetchUnseenChangelog = useCallback(() => {
    api.get('/api/changelog/unseen-count')
      .then(r => setUnseenChangelog(r.data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };

    api.get('/api/navbar-zunftrat', { headers })
      .then((r) => setIsAdmin(r.data.isZunftrat === 1))
      .catch(() => setIsAdmin(false));

    api.get('/api/navbar-admin', { headers })
      .then((r) => setIsAppAdmin(r.data.isAdmin === 1))
      .catch(() => setIsAppAdmin(false));

    fetchUnreadCount();
    fetchUnseenChangelog();
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchUnseenChangelog();
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchUnseenChangelog]);

  useEffect(() => {
    const close = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifDropdown(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (!notifDropdown) return;
    api.get('/api/notification')
      .then(r => setRecentNotifs((Array.isArray(r.data) ? r.data : []).slice(0, 5)))
      .catch(() => {});
  }, [notifDropdown]);

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      await api.post('/api/logout', { refreshToken });
    } catch {}
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    navigate('/login', { state: { message: 'Du wurdest erfolgreich abgemeldet.' } });
  };


  return (
    <>
    <nav
      className="navbar navbar-expand-lg navbar-dark bg-dark d-none d-lg-flex"
      style={{ position: 'sticky', top: 0, zIndex: 1050 }}
    >
      <div className="container">
        {/* AppName aus tblconfig hat Vorrang: so lässt sich der Anzeigename
            ohne neuen Build ändern. Fällt auf REACT_APP_ORG_NAME zurück. */}
        <Link className="navbar-brand" to="/">{appName || cfg.orgName}</Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto">
            <li className="nav-item">
              <Link className="nav-link" to="/dashboard">Dashboard</Link>
            </li>
            <li className="nav-item" ref={notifRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setNotifDropdown(v => !v)}
                className="nav-link"
                style={{ background: 'none', border: 'none', position: 'relative', padding: '8px 10px', cursor: 'pointer', color: '#adb5bd' }}
                aria-label="Mitteilungen"
              >
                <FaBell />
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.65rem' }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {notifDropdown && (
                <div style={{
                  position: 'absolute', right: 0, top: '110%', zIndex: 1060,
                  background: '#1e2130', border: '1px solid #2e3347', borderRadius: 10,
                  width: 300, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden',
                }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid #2e3347', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#e8eaf0', fontWeight: 600, fontSize: 14 }}>Mitteilungen</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => { api.post('/api/notifications/mark-all-read').catch(() => {}); setUnreadCount(0); setNotifDropdown(false); }}
                        style={{ background: 'none', border: 'none', color: '#7b82a0', fontSize: 12, cursor: 'pointer', padding: 0 }}
                      >
                        Alle gelesen
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {recentNotifs.length === 0
                      ? <div style={{ padding: '20px 14px', color: '#7b82a0', fontSize: 13, textAlign: 'center' }}>Keine Mitteilungen</div>
                      : recentNotifs.map((n, i) => (
                        <div
                          key={n.id || i}
                          onClick={() => { navigate('/notifications'); setNotifDropdown(false); setUnreadCount(0); }}
                          style={{ padding: '10px 14px', borderBottom: '1px solid #1a1f2e', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#252a3a'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ color: '#e8eaf0', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {n.title || n.Titel || n.subject || 'Mitteilung'}
                          </div>
                          <div style={{ color: '#7b82a0', fontSize: 11, marginTop: 2 }}>
                            {moment(n.created_at || n.Datum || n.date).isValid()
                              ? moment(n.created_at || n.Datum || n.date).fromNow()
                              : ''}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                  <div style={{ padding: '8px 14px', textAlign: 'center', borderTop: '1px solid #2e3347' }}>
                    <button
                      onClick={() => { navigate('/notifications'); setNotifDropdown(false); setUnreadCount(0); }}
                      style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 12, cursor: 'pointer' }}
                    >
                      Alle Mitteilungen anzeigen →
                    </button>
                  </div>
                </div>
              )}
            </li>
            <li className="nav-item">
              <Link
                className="nav-link position-relative"
                to="/changelog"
                onClick={() => setUnseenChangelog(0)}
                title="Was ist neu?"
              >
                <FaBullhorn />
                {unseenChangelog > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                    {unseenChangelog > 9 ? '9+' : unseenChangelog}
                  </span>
                )}
              </Link>
            </li>
            {isOn('Modul_Veranstaltungen') && (
              <li className="nav-item">
                <Link className="nav-link" to="/move-table">{cfg.eventTermPlural}</Link>
              </li>
            )}
            {isOn('Modul_Arbeitseinsatz') && (
              <li className="nav-item">
                <Link className="nav-link" to="/meine-einsaetze">Arbeitseinsätze</Link>
              </li>
            )}
            {isOn('Modul_Arbeitseinsatz') && (
              <li className="nav-item">
                <Link className="nav-link" to="/swap-boerse">Tauschbörse</Link>
              </li>
            )}
            {/* Die beiden Haes-Anliegen waren bisher nur ueber die
                Service-Seite erreichbar. Die zeigte dieselben Uebersichten
                wie das Profil, hier steht jetzt direkt das, was man tun will. */}
            {isOn('Modul_Haes') && (
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="#"
                  id="haesDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {cfg.costumeTerm}
                </a>
                <ul className="dropdown-menu dropdown-menu-dark" aria-labelledby="haesDropdown">
                  <li><Link className="dropdown-item" to="/service-apply-haes">{cfg.costumeTerm} beantragen</Link></li>
                  <li><Link className="dropdown-item" to="/service-order-spare-parts">{cfg.costumeTerm}-Einzelteile bestellen</Link></li>
                </ul>
              </li>
            )}
            <li className="nav-item">
              <Link className="nav-link" to="/abstimmungen">Abstimmungen</Link>
            </li>
            {isOn('Modul_Tasks') && (
              <li className="nav-item">
                <Link className="nav-link" to="/meine-aufgaben">Aufgaben</Link>
              </li>
            )}
            <li className="nav-item">
              <Link className="nav-link" to="/downloads">Downloads</Link>
            </li>

            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="profilDropdown"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FaUser className="me-1" /> Profil
              </a>
              <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end" aria-labelledby="profilDropdown">
                <li><Link className="dropdown-item" to="/meine-daten"><FaUser className="me-2" />Meine Daten</Link></li>
                <li><Link className="dropdown-item" to="/my-qr-code"><FaQrcode className="me-2" />Mein QR Code</Link></li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={handleLogout}>
                    <FaSignOutAlt className="me-2" />Abmelden
                  </button>
                </li>
              </ul>
            </li>

            {isAdmin && (
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="#"
                  id="navbarDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {cfg.adminRoleName}
                </a>
                <ul className="dropdown-menu dropdown-menu-dark" aria-labelledby="navbarDropdown">
                  {/* Gruppiert nach Gegenstand: Menschen, Veranstaltungen,
                      Ausstattung, Einsaetze, Beteiligung, Verwaltung. */}
                  <li><Link className="dropdown-item" to="/zr-notification">Mitteilungen</Link></li>
                  <li><Link className="dropdown-item" to="/zr-member">{cfg.memberTermPlural} verwalten</Link></li>

                  {isOn('Modul_Veranstaltungen') && <>
                    <li><hr className="dropdown-divider" /></li>
                    <li><Link className="dropdown-item" to="/zr-moves">{cfg.eventTermPlural} verwalten</Link></li>
                    <li><Link className="dropdown-item" to="/zr-calendar">Kalender</Link></li>
                    <li><Link className="dropdown-item" to="/zr-qr-reader"><FaQrcode className="me-1" /> QR-Codes bestätigen</Link></li>
                  </>}
                  {isOn('Modul_Haes') && <>
                    <li><hr className="dropdown-divider" /></li>
                    <li><Link className="dropdown-item" to="/zr-haes">{cfg.costumeTermPlural} verwalten</Link></li>
                    <li><Link className="dropdown-item" to="/zr-costume-type-managers">{cfg.costumeTerm}typen &amp; Manager</Link></li>
                  </>}
                  {isOn('Modul_Arbeitseinsatz') && <>
                    <li><hr className="dropdown-divider" /></li>
                    <li><Link className="dropdown-item" to="/zr-workassignment">Arbeitseinsätze verwalten</Link></li>
                    <li><Link className="dropdown-item" to="/zr-swap-boerse">Tauschbörse prüfen</Link></li>
                  </>}

                  <li><hr className="dropdown-divider" /></li>
                  <li><Link className="dropdown-item" to="/zr-polls">Abstimmungen verwalten</Link></li>
                  {isOn('Modul_Tasks') && (
                    <li><Link className="dropdown-item" to="/zr-aufgaben">Aufgabenverwaltung</Link></li>
                  )}

                  <li><hr className="dropdown-divider" /></li>
                  <li><Link className="dropdown-item" to="/zr-download">Dokumente bereitstellen</Link></li>
                  {isOn('Modul_Berichte') && (
                    <li><Link className="dropdown-item" to="/zr-statistics">Statistiken</Link></li>
                  )}
                  <li><Link className="dropdown-item" to="/zr-audit-log">Aktions-Log (DSGVO)</Link></li>
                  <li><Link className="dropdown-item" to="/zr-license"><FaKey className="me-1" /> Lizenz</Link></li>
                </ul>
              </li>
            )}

            {isAppAdmin && (
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle text-danger"
                  href="#"
                  id="adminDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Admin
                </a>
                <ul className="dropdown-menu dropdown-menu-dark" aria-labelledby="adminDropdown">
                  <li><Link className="dropdown-item" to="/admin-analytics"><FaChartBar className="me-1" /> Analytik &amp; System</Link></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><Link className="dropdown-item" to="/admin-features"><FaPuzzlePiece className="me-1" /> Feature-Verwaltung</Link></li>
                  <li><Link className="dropdown-item" to="/admin-config"><FaCogs className="me-1" /> App-Konfiguration</Link></li>
                  <li><Link className="dropdown-item" to="/admin-forms"><FaWpforms className="me-1" /> Formulare gestalten</Link></li>
                  <li><Link className="dropdown-item" to="/admin-changelog"><FaBullhorn className="me-1" /> Changelog</Link></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><Link className="dropdown-item" to="/admin-qr-scan-log"><FaQrcode className="me-1" /> QR-Code Audit-Log</Link></li>
                </ul>
              </li>
            )}

          </ul>
        </div>
      </div>
    </nav>

    {/* Lizenzwarnung für den Zunftrat. Mitglieder sehen sie bewusst nicht —
        sie können ohnehin nichts veranlassen. */}
    {isAdmin && lizenz?.enforced && ['expiring', 'expired'].includes(lizenz.status) && (
      <Link to="/zr-license" style={{ textDecoration: 'none' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.55rem 1rem',
          background: lizenz.status === 'expired' ? 'rgba(248,113,113,0.14)' : 'rgba(240,165,0,0.13)',
          borderBottom: `1px solid ${lizenz.status === 'expired' ? 'rgba(248,113,113,0.35)' : 'rgba(240,165,0,0.35)'}`,
          color: lizenz.status === 'expired' ? '#fca5a5' : '#ffcb57',
          fontSize: '0.86rem',
        }}>
          <FaExclamationTriangle style={{ flex: '0 0 auto' }} />
          <span>
            {lizenz.status === 'expired'
              ? <>Die Lizenz ist <b>abgelaufen</b>{lizenz.expDate ? ` (${lizenz.expDate})` : ''}. Die App ist für {cfg.memberTermPlural} nicht mehr nutzbar.</>
              : <>Die Lizenz läuft in <b>{lizenz.daysLeft} Tagen</b> ab{lizenz.expDate ? ` (${lizenz.expDate})` : ''}.</>}
          </span>
          <span style={{ marginLeft: 'auto', opacity: 0.75, whiteSpace: 'nowrap' }}>Details →</span>
        </div>
      </Link>
    )}

    <BottomNav />
    </>
  );
};

export default CustomNavbar;
